import Link from 'next/link'
import type { Tag } from '../payload-types'
import { CHIP, SECTION_EYEBROW, TAIL_RULE } from './chip'

/**
 * I tag dell'articolo, in coda al corpo e SOPRA le fonti.
 *
 * ERA UNA FILA DI PILLOLE SENZA NOME dentro <ArticleView>: `mt-10 flex flex-wrap gap-2` e via, senza
 * titolo e senza filetto, quindi la coda dell'articolo passava dalle fonti a una manciata di
 * `#parole` di cui la pagina non diceva che cosa fossero né dove portassero. Ora è una sezione come
 * le altre — filetto, occhiello, chip — e le tre code dell'articolo (informazioni pratiche, tag,
 * fonti) si leggono come tre cose distinte invece che come un residuo.
 *
 * PRIMA DELLE FONTI, e non è indifferente: i tag sono navigazione (portano dentro il sito), le fonti
 * sono la chiusura documentaria dell'articolo (portano fuori, con `nofollow`). Chi finisce di leggere
 * incontra prima il «dove vado adesso» e poi il «da dove viene questo».
 *
 * SE NON CI SONO TAG NON RENDE NIENTE, al contrario di <SourcesBlock> che dichiara l'assenza. Non è
 * un'incoerenza: un articolo senza fonti registrate è una pagina che afferma cose senza dire da dove
 * vengano, e il silenzio la farebbe sembrare documentata; un articolo senza tag non ha nulla da
 * confessare al lettore, gli manca solo un modo in più per navigare.
 */
export default function TagsBlock({ tags }: { tags: Tag[] }) {
  const list = tags.filter((t) => t.slug)

  if (!list.length) return null

  return (
    // `aria-label` e non `aria-labelledby`: lo scroll infinito monta N <ArticleView> nella stessa
    // pagina, quindi un id fisso sarebbe duplicato N volte (vedi <SourcesBlock> e <PracticalBox>).
    // NIENTE MARGINE QUI: la distanza fra i blocchi della coda la tiene il contenitore in
    // <ArticleView>, che è l'unico a sapere quali di questi blocchi esistono su questo articolo.
    <section className={TAIL_RULE} aria-label="Tag">
      <h2 className={SECTION_EYEBROW}>Tag</h2>
      {/* `role="list"` esplicito: con `display: flex` Safari/VoiceOver toglie all'elemento il ruolo
          di lista, e chi ascolta perde il «3 elementi». */}
      <ul role="list" className="mt-3 flex flex-wrap gap-2">
        {list.map((t) => (
          <li key={t.id}>
            {/* Il `#` resta: è il segno che distingue un tag da una fonte ora che il chip è lo
                stesso, e dice che la parola è un'etichetta e non il titolo di qualcosa. */}
            <Link href={`/tag/${t.slug}/`} className={CHIP}>
              #{t.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
